
import Modal from '../shared/Modal'

interface BulkEditConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (applyToAll: boolean) => void
  field: 'owner' | 'broker'
  originalValue: string
  newValue: string
  matchingCount: number
  currentAircraftSerial: string
}

export default function BulkEditConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  field,
  originalValue,
  newValue,
  matchingCount,
  currentAircraftSerial
}: BulkEditConfirmationModalProps) {
  const handleUpdateAll = () => {
    onConfirm(true)
    onClose()
  }

  const handleUpdateOnlyThis = () => {
    onConfirm(false)
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Bulk Edit Confirmation" size="medium">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Multiple Aircraft Found
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  We found <strong>{matchingCount}</strong> other aircraft with the same {field}: 
                  <span className="font-mono bg-white px-2 py-1 rounded ml-1">"{originalValue}"</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Change Details:</h4>
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current Aircraft:</span>
                <span className="text-sm font-mono">{currentAircraftSerial}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Field:</span>
                <span className="text-sm font-medium capitalize">{field}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600">From:</span>
                  <span className="text-sm font-mono bg-red-100 px-2 py-1 rounded text-right max-w-xs break-words">
                    {originalValue}
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-sm text-gray-600">To:</span>
                  <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded text-right max-w-xs break-words">
                    {newValue}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">What would you like to do?</h4>
            <p className="text-sm text-gray-600 mb-4">
              Choose whether to apply this change to all matching aircraft or just the current one.
            </p>
          </div>
        </div>

        <div className="flex flex-col space-y-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleUpdateAll}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Update All {matchingCount + 1} Aircraft</span>
          </button>
          
          <button
            type="button"
            onClick={handleUpdateOnlyThis}
            className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Update Only This Aircraft</span>
          </button>
          
          <button
            type="button"
            onClick={handleCancel}
            className="w-full px-4 py-3 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
